import { css, type SerializedStyles } from '@emotion/core'
import React, { createContext, useContext, type ReactNode, useMemo } from 'react'
import { skeletonCss } from '@fs/zion-ui/dist/es/skeleton/BaseSkeleton'

const flexCss = css`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
`

const getAnimationDelayCss = (delay: number): SerializedStyles => css`
  & .${skeletonCss} {
    /* !important needed to override the library class at equal specificity */
    animation-delay: ${delay}s !important;
  }
`

const SkeletonAnimationSyncContext = createContext(false)

type Props = {
  children: ReactNode
}

/**
 * To synchronize the animation of multiple skeletons on the same page, this decorator captures the
 * current time at mount and applies it as an offset to the animation delay. This way, all skeletons
 * wrapped in this decorator will have their animations in sync, regardless of when they are rendered.
 * If there are nested decorators, only the outermost one will apply the animation delay to prevent
 * compounding offsets.
 *
 * This is useful for pages with multiple remounts of the same skeleton.
 *
 * Note: Zion UI skeleton animation is "0.6s infinite alternate", which means it pulses forward
 * for 0.6s and backward for 0.6s, creating a full cycle of 1.2s.
 */
export default function SkeletonAnimationSyncDecorator({ children }: Props): JSX.Element {
  const hasAncestor = useContext(SkeletonAnimationSyncContext)

  // Captured once at mount so re-renders don't shift the animation offset
  // Only apply if there's no ancestor decorator
  // Full cycle is 1.2s because of the "alternate" direction (0.6s forward + 0.6s backward)
  // Since Date uses milliseconds, we use % 1200ms and then convert to seconds with the / 1000 operation to get the offset within the 1.2s cycle
  const animationDelayCss = useMemo(
    () => (hasAncestor ? undefined : getAnimationDelayCss(-(Date.now() % 1200) / 1000)),
    [hasAncestor]
  )

  return (
    <SkeletonAnimationSyncContext.Provider value>
      <div css={[animationDelayCss, flexCss]}>{children}</div>
    </SkeletonAnimationSyncContext.Provider>
  )
}
